import { z } from 'zod';

/**
 * Mirrors `src/auth/session-store.ts`, but the admin session is a separate
 * world: its own storage key, no `businessId`/`role`, and never shares
 * in-memory state with the owner session store. Cookie-or-bearer transport
 * is identical to the business flow (see that file's header comment).
 */

const STORAGE_KEY = 'dale-admin-session';
const StoredAdminSessionSchema = z.object({
  transport: z.enum(['cookie', 'bearer']),
  token: z.string().min(1).optional(),
  adminId: z.string().min(1),
  username: z.string().min(1),
  expiresAt: z.string().datetime(),
  mustChangePassword: z.boolean().optional(),
}).refine((session) => session.transport !== 'bearer' || Boolean(session.token));

export type AdminSessionTransport = 'cookie' | 'bearer';

export type StoredAdminSession = {
  transport: AdminSessionTransport;
  /** Present only for bearer sessions; a cookie session has no client-held secret. */
  token?: string;
  adminId: string;
  username: string;
  expiresAt: string;
  /** True on first login or after a reset; blocks the whole admin panel until resolved. */
  mustChangePassword?: boolean;
};

let current: StoredAdminSession | null = null;

export function getAdminSession(): StoredAdminSession | null {
  return current;
}

export function setAdminSession(session: StoredAdminSession): void {
  current = session;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearAdminSession(): void {
  current = null;
  sessionStorage.removeItem(STORAGE_KEY);
}

/** Flips `mustChangePassword` off after a successful password change, without mutating the previous record. */
export function markAdminPasswordChanged(): StoredAdminSession | null {
  if (!current) return null;
  const next: StoredAdminSession = { ...current, mustChangePassword: false };
  setAdminSession(next);
  return next;
}

/** Hydrates the in-memory session from sessionStorage; drops an expired one. */
export function restoreAdminSession(): StoredAdminSession | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) { current = null; return null; }

  current = null;
  try {
    const parsed = StoredAdminSessionSchema.safeParse(JSON.parse(raw));
    if (parsed.success && Date.parse(parsed.data.expiresAt) > Date.now()) {
      current = parsed.data;
      return current;
    }
  } catch {
    // Invalid stored JSON is treated as a signed-out session.
  }
  clearAdminSession();
  return null;
}
